import type { ComponentType, PropsWithChildren, ReactElement } from 'react';
import { sortPackages } from 'packages/common/sorters/sortPackages';
import type {
  MeetingPackage,
  CelebrationPackage,
  VenuePackage
} from 'packages/types';

type PackageWithCategory = MeetingPackage | CelebrationPackage | VenuePackage;

// Row props travel as data so `Package` can stay a stable module-level
// reference — it is a JSX element type, and a fresh identity per render
// remounts every row, losing `PackageContainer`'s accordion state.
// `RowProps` is generic rather than one fixed shape because each list's row
// requires a different, sometimes-required slice of it (e.g. only the venue
// and meeting rows require `allServices`) — tying `Package` and
// `packageProps` to the same type parameter lets each call site prove its
// own row's requirements instead of every row settling for the loosest one.
type Props<
  Pkg extends PackageWithCategory,
  RowProps extends object = object
> = {
  packages: Pkg[];
  categoryLabels: Record<Pkg['category'], string>;
  PackageGroup: ComponentType<PropsWithChildren<{ categoryLabel: string }>>;
  Package: ComponentType<{ pkg: Pkg } & RowProps>;
  packageProps?: RowProps;
};

export function PackagesByCategory<
  Pkg extends PackageWithCategory,
  RowProps extends object = object
>(props: Props<Pkg, RowProps>): ReactElement | null {
  if (!props.packages) {
    return null;
  }

  // Object.keys widens to string[] regardless of the source type, so the
  // categories need casting back to the label record's own key type.
  const categories = Object.keys(props.categoryLabels) as Pkg['category'][];

  const packageGroupsInPredeterminedOrder = categories
    .map(category => ({
      category,
      packages: props.packages.filter(pkg => pkg.category === category),
      categoryLabel: props.categoryLabels[category]
    }))
    .filter(({ packages }) => packages.length > 0);

  return (
    <>
      {packageGroupsInPredeterminedOrder.map(
        ({ category, categoryLabel, packages }) => (
          <props.PackageGroup key={category} categoryLabel={categoryLabel}>
            {sortPackages(packages).map(pkg => (
              // TS can't verify a generic spread reconstructs `{ pkg: Pkg }
              // & RowProps` from `RowProps` alone — true for every
              // instantiation the public signature above actually allows,
              // just not something the checker can prove inside a generic
              // body.
              <props.Package
                key={pkg.id}
                {...(props.packageProps as RowProps)}
                pkg={pkg}
              />
            ))}
          </props.PackageGroup>
        )
      )}
    </>
  );
}
